export interface CoreContent {
  definition: string;
  mechanism: string;
  derivation: string;
}

/** Individually reviewed content for the initial high-use concepts. */
export const CORE_CONTENT: Readonly<Record<string, CoreContent>> = {
  'fed-funds-rate': {
    definition: 'The federal funds rate is the overnight unsecured rate at which eligible U.S. depository institutions lend reserve balances.',
    mechanism: 'The Fed guides it with a target range and administered rates; changes reach funding, credit, discount rates, and the dollar with variable lags.',
    derivation: 'Use the New York Fed effective rate in annual percentage points with observation date and release vintage.',
  },
  'breakeven-inflation': {
    definition: 'Breakeven inflation is the rate that equalizes returns on comparable nominal Treasuries and inflation-protected Treasuries.',
    mechanism: 'The spread includes inflation expectations, liquidity differences, and risk premia, so it is not a pure inflation forecast.',
    derivation: 'Subtract the matched-maturity TIPS real yield from the nominal yield using synchronized observations.',
  },
  'real-policy-rate': {
    definition: 'The real policy rate is the nominal policy rate less a declared inflation measure or expectation.',
    mechanism: 'It approximates purchasing-power restraint, but ex-ante breakeven and ex-post CPI versions answer different questions.',
    derivation: 'Subtract the named inflation measure from effective fed funds and identify the result as ex-ante or ex-post.',
  },
  'gdp-trend': {
    definition: 'GDP trend describes the pace and direction of inflation-adjusted economic production beyond one noisy quarterly release.',
    mechanism: 'Consumption, investment, government, and net exports contribute differently, while revisions can materially alter the first estimate.',
    derivation: 'Use BEA real GDP with vintage and state whether growth is annualized quarterly or year over year.',
  },
  'unemployment-rate': {
    definition: 'The unemployment rate is the share of the labor force without work that is available for and actively seeking employment.',
    mechanism: 'It lags activity and is participation-sensitive, so stable U-3 can coexist with weaker hours, hiring, or labor-force exits.',
    derivation: 'Use BLS household-survey U-3 and document seasonal adjustment and release vintage.',
  },
  'yield-curve-10y-2y': {
    definition: 'The 10-year minus 2-year curve is the spread between long- and short-dated U.S. Treasury yields.',
    mechanism: 'Inversion can reflect policy restraint; re-steepening from expected cuts differs from re-steepening caused by rising term premium.',
    derivation: 'Subtract synchronized 2-year constant-maturity yield from 10-year yield and report basis points.',
  },
  'term-premium': {
    definition: 'Term premium is estimated compensation for holding a long bond instead of repeatedly rolling short bonds.',
    mechanism: 'It is latent, model-dependent, and responds to inflation uncertainty, duration supply, demand, and risk appetite.',
    derivation: 'Use a named model and vintage, report percentage points, and never label the estimate as a traded quote.',
  },
  'liquidity-conditions': {
    definition: 'Liquidity conditions describe how readily funding and balance-sheet capacity support transactions without large price concessions.',
    mechanism: 'Central-bank balances, Treasury cash, dealer capacity, policy rates, and private credit matter, making one net-liquidity identity incomplete.',
    derivation: 'Declare every component, transformation, frequency, currency, and point-in-time release before aggregation.',
  },
  vix: {
    definition: 'VIX is a model-free option-market estimate of expected 30-day S&P 500 variance, quoted as annualized volatility.',
    mechanism: 'It includes a volatility risk premium; persistence and term structure carry more information than the fear-gauge nickname.',
    derivation: 'Use Cboe methodology and official close, and distinguish the index from VIX futures and ETPs.',
  },
  'spot-versus-etf': {
    definition: 'Spot versus ETF separates an underlying cash-market price from a fund intended to track related exposure.',
    mechanism: 'Holdings, fees, roll yield, market hours, tax, and creation-redemption friction can make the vehicle diverge from the underlying.',
    derivation: 'Compare synchronized series after documenting holdings, benchmark, fees, roll method, and return convention.',
  },
  'gold-signal': {
    definition: 'Gold as signal treats price behavior as evidence about real rates, reserve demand, currency confidence, and tail risk.',
    mechanism: 'Those drivers can offset each other, so rates, the dollar, official buying, and positioning must confirm the interpretation.',
    derivation: 'Name spot or futures benchmark and currency, then compare matched-window returns with real rates and FX.',
  },
  'oil-signal': {
    definition: 'Oil as signal uses crude prices and curve structure to assess physical tightness, growth demand, and supply risk.',
    mechanism: 'Outage rallies differ from demand rallies, while grade, location, product cracks, and futures structure can overturn a benchmark read.',
    derivation: 'Name Brent or WTI, contract month, settlement source, currency, and spot or constant-maturity method.',
  },
  'ten-year-yield': {
    definition: 'The 10-year Treasury yield is a benchmark long-duration U.S. government rate used throughout asset pricing.',
    mechanism: 'Expected policy, inflation compensation, term premium, and safe-asset demand can produce identical moves with different meanings.',
    derivation: 'Use a named constant-maturity or on-the-run series with percentage units and close timestamp.',
  },
  'dollar-index-proxy': {
    definition: 'A dollar index proxy summarizes the U.S. dollar against a specified basket rather than measuring a universal dollar price.',
    mechanism: 'Weights can omit key partners or overstate Europe, and ETFs add fees and market-hours effects.',
    derivation: 'Publish the index or ETF, basket weights, return convention, and synchronized observation times.',
  },
  'managed-conflict-vs-crisis': {
    definition: 'Managed conflict versus crisis classifies whether escalation remains bounded by principals’ incentives or is breaking those constraints.',
    mechanism: 'Costly actions, disrupted flows, abandoned carve-outs, and crossed cannot-lose lines matter more than headlines.',
    derivation: 'Apply the versioned rubric to independent sourced signals and preserve unresolved or unavailable evidence.',
  },
  'energy-basket': {
    definition: 'An energy basket combines crude, products, gas, or related exposures to inspect the complex beyond one contract.',
    mechanism: 'Regional logistics, weather, inventories, weights, and roll rules can dominate an aggregate return.',
    derivation: 'Publish constituents, weights, units, currency conversion, rebalancing, and futures-roll convention.',
  },
  'growth-metals': {
    definition: 'Growth metals are industrial metals sensitive to construction, manufacturing, electrification, and capital spending.',
    mechanism: 'Supply disruptions, inventories, Chinese policy, and currencies can overwhelm the activity signal from copper and peers.',
    derivation: 'Use named contracts, normalized returns, synchronized closes, and inventory context.',
  },
  'agriculture-basket': {
    definition: 'An agriculture basket combines crops or soft commodities to observe food, weather, and farm-input pressure.',
    mechanism: 'Seasonality, storage, geography, and policy differ by crop, so an aggregate can hide a localized shortage.',
    derivation: 'Declare contracts, crop years, weights, roll schedule, and price or total-return basis.',
  },
  'precious-metals': {
    definition: 'A precious-metals basket combines monetary and industrial metals such as gold, silver, platinum, and palladium.',
    mechanism: 'Reserve demand and industrial demand differ, making relative performance as important as the aggregate.',
    derivation: 'Publish constituents, weights, currency, benchmark timestamps, and any futures-roll effect.',
  },
  'broad-commodity-baskets': {
    definition: 'A broad commodity basket aggregates energy, metals, and agriculture into a rules-based return measure.',
    mechanism: 'Energy weight, contract selection, collateral return, and roll yield can dominate the headline.',
    derivation: 'Name the index version, weights, rebalance schedule, and excess- or total-return basis.',
  },
  'inventory-and-curve-structure': {
    definition: 'Inventory and curve structure pair physical stocks with the relationship between near and deferred futures prices.',
    mechanism: 'Backwardation often rewards immediate supply while contango rewards storage, subject to financing, quality, and location.',
    derivation: 'Match inventory location and grade to named contract months and report spread units and timestamps.',
  },
  'reserve-currency': {
    definition: 'A reserve currency is widely held and used for settlement, funding, invoicing, and collateral beyond its home economy.',
    mechanism: 'Deep safe assets, convertibility, institutions, rails, and network effects make status broader than bilateral settlement.',
    derivation: 'Name the measure: official reserves, liabilities, invoice shares, and payment shares are not interchangeable.',
  },
  'managed-currency': {
    definition: 'A managed currency is influenced through fixes, bands, intervention, controls, or official guidance.',
    mechanism: 'Management can suppress spot volatility while moving pressure into reserves, forwards, offshore rates, or controls.',
    derivation: 'Compare the declared mechanism with onshore, offshore, reserve, and forward evidence at synchronized times.',
  },
  'fix-versus-spot': {
    definition: 'Fix versus spot compares an authority’s reference rate with the market price around that reference.',
    mechanism: 'The gap can reveal intent or stress, but venue, time, daily noise, and onshore restrictions matter.',
    derivation: 'Subtract the published fix from a named spot quote at a documented time using consistent direction.',
  },
  'commodity-currency': {
    definition: 'A commodity currency belongs to an economy materially exposed to commodity export income and terms of trade.',
    mechanism: 'Rates, fiscal policy, hedging, imports, and capital flows can break a historically stable commodity beta.',
    derivation: 'Estimate rolling relationships against the relevant export basket and test stability across regimes.',
  },
  'crisis-currency-pattern': {
    definition: 'A crisis-currency pattern combines depreciation, funding stress, reserve pressure, and market dislocation.',
    mechanism: 'Basis, forwards, reserves, controls, and credit distinguish deliberate devaluation from disorderly external stress.',
    derivation: 'Apply a declared multi-signal rule with synchronized FX, funding, reserve, and sovereign-credit observations.',
  },
  'market-breadth': {
    definition: 'Market breadth measures how many securities participate in a move rather than relying on a weighted index.',
    mechanism: 'Advance-decline, new-high, and moving-average measures differ; weak breadth signals vulnerability, not exact timing.',
    derivation: 'State universe, corporate-action and delisting rules, frequency, and exact participation formula.',
  },
  'sector-rotation': {
    definition: 'Sector rotation is changing relative leadership among stable groups of companies over declared horizons.',
    mechanism: 'Growth, rates, commodities, defensiveness, and rebalancing can drive it, while one-day leadership is weak evidence.',
    derivation: 'Compare synchronized total returns against a named benchmark over multiple predeclared windows.',
  },
  'passive-flow': {
    definition: 'Passive flow is buying or selling caused by fund rules, index events, contributions, redemptions, or rebalancing.',
    mechanism: 'It can move price without new fundamentals, though many flows and their causal price effects are only estimated.',
    derivation: 'Name vehicle and event, estimate dollars from disclosed assets and weights, and label measured versus inferred flow.',
  },
  'ownership-concentration': {
    definition: 'Ownership concentration measures how much of a security or system is controlled by a small holder set.',
    mechanism: 'Patient sponsorship, governance, crowded exits, and forced-flow risk depend on holder type and beneficial ownership.',
    derivation: 'Aggregate point-in-time filings under a documented method and report top-N share, concentration, and filing lag.',
  },
  'insider-activity': {
    definition: 'Insider activity covers reportable purchases, sales, grants, exercises, and dispositions by company insiders.',
    mechanism: 'Open-market purchases differ from tax sales or plans; codes, prior holdings, and compensation context are essential.',
    derivation: 'Classify filed transaction codes and dates, normalize by holdings, and retain amendments.',
  },
  'short-interest': {
    definition: 'Short interest is the quantity of shares sold short and not covered at a reporting date.',
    mechanism: 'Borrow cost, float, liquidity, and reporting lag determine whether high short interest signals skepticism or squeeze fuel.',
    derivation: 'Divide reported shorts by defined float and calculate days to cover from matched average volume.',
  },
  'correlation-and-factor-exposure': {
    definition: 'Correlation and factor exposure describe shared return movement and sensitivity to systematic drivers.',
    mechanism: 'Both vary by model, window, and regime, and apparently diverse holdings can share one hidden thesis.',
    derivation: 'Declare total returns, frequency, window, benchmark, factors, missing-data treatment, and uncertainty.',
  },
  principals: {
    definition: 'Principals are the three to seven actors whose choices can materially change a geopolitical game.',
    mechanism: 'Selection follows decision power rather than visibility and records each actor’s constraints, leverage, and next move.',
    derivation: 'Document decision authority and sourced independent leverage; justify maps with more than seven principals.',
  },
  'cannot-lose-line': {
    definition: 'A cannot-lose line is an outcome a principal must prevent to preserve survival, legitimacy, security, or access.',
    mechanism: 'Costly behavior and durable incentives reveal the line more reliably than rhetoric.',
    derivation: 'Triangulate actions, commitments, and resource allocation and record contrary evidence that would move the line.',
  },
  'hidden-constraint': {
    definition: 'A hidden constraint is a binding limit on an actor that is absent from its public position.',
    mechanism: 'Funding, coalition, logistics, law, legitimacy, or host dependence can shrink the feasible move set.',
    derivation: 'Infer from sourced capacity and repeated behavior while retaining competing explanations and falsifiers.',
  },
  leverage: {
    definition: 'Geopolitical leverage is the credible ability to change another principal’s payoff or available choices.',
    mechanism: 'Control matters only with limited substitutes, favorable timing, credible use, and tolerable reciprocal cost.',
    derivation: 'Map dependency, action, target cost, owner cost, substitutes, and horizon with evidence for each link.',
  },
  'managed-conflict': {
    definition: 'Managed conflict is confrontation whose principals still bound escalation because breakdown violates core interests.',
    mechanism: 'Pressure can coexist with protected energy, finance, settlement, or communication carve-outs.',
    derivation: 'Track escalation beside preserved channels and require independent costly signals before changing regime.',
  },
  dcf: {
    definition: 'Discounted cash flow values an asset from explicit future free cash flow and terminal value.',
    mechanism: 'It makes growth, reinvestment, duration, and required return auditable but remains terminal-value sensitive.',
    derivation: 'Discount each projected FCF and terminal value, subtract net debt, and divide by diluted shares.',
  },
  epv: {
    definition: 'Earnings Power Value capitalizes sustainable current earnings without paying for future growth.',
    mechanism: 'Normalization and maintenance investment distinguish franchise value from cyclical or accounting noise.',
    derivation: 'Capitalize normalized after-tax operating earnings, adjust assets and debt, and divide by shares.',
  },
  'reverse-dcf': {
    definition: 'Reverse DCF solves for growth, margin, or required return implied by the current market price.',
    mechanism: 'It asks what must be true but still depends on every assumption held fixed during the solve.',
    derivation: 'Set modeled equity value equal to market capitalization and solve one variable within disclosed bounds.',
  },
  'spm-dcf': {
    definition: 'SPM-DCF is a visible waterfall from raw DCF through separately sourced structural-power adjustments.',
    mechanism: 'Fixed-order, switchable layers prevent doctrine from hiding in an unexplained discount-rate plug.',
    derivation: 'Apply each enabled percentage layer sequentially and retain source, before value, and after value.',
  },
  'probability-of-ruin': {
    definition: 'Probability of ruin estimates the chance a portfolio breaches a defined survival threshold before the horizon.',
    mechanism: 'Volatility, correlation, leverage, withdrawals, and liquidity make ruin path-dependent despite positive expected return.',
    derivation: 'Model paths under declared distributions and count threshold breaches, reporting horizon, seed, and sensitivities.',
  },
  'brier-score': {
    definition: 'Brier score is mean squared error between a probability forecast and its binary outcome.',
    mechanism: 'It rewards calibrated confidence but mixes calibration with resolution and depends on the event base rate.',
    derivation: 'Average (forecast probability minus zero-or-one outcome) squared across the declared point-in-time sample.',
  },
  calibration: {
    definition: 'Calibration asks whether events forecast at a probability occur at approximately that frequency.',
    mechanism: 'A base-rate forecast may be calibrated but uninformative, and small clustered samples need uncertainty bands.',
    derivation: 'Use predeclared bins or a reliability model on holdout forecasts and compare forecast with observed frequency.',
  },
  'valid-at': {
    definition: 'Valid-at is when a fact or observation applies in the world being modeled.',
    mechanism: 'It differs from knowledge and storage time, preventing revisions from rewriting a historical decision state.',
    derivation: 'Use primary-source effective or observation period with timezone and granularity.',
  },
  'known-at': {
    definition: 'Known-at is the earliest time Atlas could legitimately have used a piece of information.',
    mechanism: 'It excludes later releases and corrections from historical replay, even when they describe an earlier period.',
    derivation: 'Use earliest verifiable system availability and never backdate revised knowledge.',
  },
};
